// vendors
import React from "react"
import { css } from "@emotion/core"
import { graphql, Link } from "gatsby"

// components
import Layout from "../components/Layout"
import Seo from "../components/Seo"

// utils
import { fontSizes, colors, fonts } from "../styles/variables"
import formatTime from "../utils/format-time"
import mediaQuery from "../utils/media-query"
import wrapper from "../utils/wrapper"

// images
import illustrationPath from "../images/illustration-path.svg"
import illustrationEye from "../images/illustration-eye.svg"
import illustrationCellphoneFacebook from "../images/illustration-cellphoneFacebook.svg"
import IllustrationAfficheTournee from "../images/IllustrationAfficheTournee"

const EventCard = ({ city, from, to, location, local, url }) => (
  <Link
    to={url}
    css={css`
      display: flex;
      justify-content: center;
      align-items: center;
      text-decoration: none;
      color: inherit;

      :before {
        padding-bottom: 100%;
        display: block;
        content: "";
      }

      &:nth-child(2n) {
        background-color: ${colors.blueyGreen};
      }

      &:nth-child(2n + 1) {
        background-color: ${colors.dullYellow};
        border-radius: 50%;
      }

      &:first-child {
        background-color: ${colors.pastelRed};
      }
    `}
  >
    <div
      css={css`
        text-align: center;
        width: 75%;
      `}
    >
      <p
        css={css`
          font-size: ${fontSizes[3] / fontSizes[1]}em;
          font-weight: bold;
          font-family: ${fonts.accent};
        `}
      >
        {city}
      </p>

      <p
        css={css`
          font-size: ${fontSizes[5] / fontSizes[1]}em;
          font-weight: bold;
          margin: ${fontSizes[5] / fontSizes[5]}em auto;
          text-transform: uppercase;
        `}
      >
        <span
          css={css`
            font-size: ${fontSizes[9] / fontSizes[5]}em;
            font-weight: 900;
            display: block;
            line-height: 1;
          `}
        >
          {new Date(from).toLocaleDateString("fr", {
            day: "2-digit",
          })}
        </span>
        {new Date(from).toLocaleDateString("fr", {
          month: "long",
        })}
      </p>

      <p>
        {formatTime(from)}
        {to && <span> à {formatTime(to)}</span>}
        <br />
        {location}
        {local && <span> - {local}</span>}
      </p>
    </div>
  </Link>
)

const TourneeDesStagePage = ({ data }) => {
  const breakpoint = 1024
  const rawEvents = (data.events || []).edges || []
  const now = new Date()
  let calendarYear = undefined

  const events =
    rawEvents
      .map(({ node }) => {
        console.log(node)
        return {
          city: (node.address || {}).city || null,
          from: node.fromDate || null,
          to: node.toDate || null,
          location: node.placeName || null,
          local: (node.address || {}).local || null,
          url: `/evenements/${((node.meta || {}).slug || {}).current}`,
        }
      })
      .filter((event) => {
        const eventDate = new Date(event.to)

        return (
          (eventDate.getFullYear() >= now.getFullYear() &&
            eventDate.getMonth() >= now.getMonth()) ||
          (eventDate.getFullYear() >= now.getFullYear() &&
            eventDate.getMonth() >= now.getMonth() &&
            eventDate.getDate() >= now.getDate())
        )
      }) || []

  if (calendarYear === undefined && events.length > 0) {
    calendarYear = new Date(events[0].from).getFullYear()
  } else {
    const lastEvent = new Date(rawEvents[rawEvents.length - 1].node.fromDate)

    calendarYear =
      lastEvent.getFullYear() < now.getFullYear()
        ? now.getFullYear()
        : now.getFullYear() + 1
  }

  return (
    <Layout>
      <Seo
        description="Sarros vient à votre rencontre! Apprenez-en plus sur notre tournée des stages. Voyez quand l'équipe SARROS se déplacera chez vous à l'aide de ce calendrier."
        lang="fr"
        keywords={["calendrier stages en santé"]}
        title="La tournée des stages: calendrier stages en santé"
      />

      <article>
        <section
          css={css`
            ${wrapper.bolt()}
            ${mediaQuery.greaterThen(breakpoint)} {
              display: grid;

              grid-template-columns: minmax(260px, 538px) minmax(260px, auto);
              grid-auto-rows: auto;
              align-items: end;
              grid-column-gap: 20px;
            }
          `}
        >
          <h1
            css={css`
              ${mediaQuery.greaterThen(breakpoint)} {
                grid-column: 1 / span 1;
                grid-row: 1 / span 1;
                ${wrapper.paddingLeft()}
              }
            `}
          >
            La tournée des stages d'été
          </h1>

          <div
            css={css`
              grid-column: span 1;
              grid-row: span 2;
            `}
          >
            <p
              css={css`
                font-size: ${fontSizes[5] / fontSizes[1]}em;
                line-height: ${fontSizes[6] / fontSizes[5]};
                font-weight: 600;
              `}
            >
              La Tournée des Stages, c’est le moment où l’équipe SARROS va à la
              rencontre des étudiants en médecine.
            </p>

            <p>
              C’est l’occasion pour tous les futurs médecins de discuter avec
              des représentants de chaque région SARROS pour découvrir de
              nouvelles perspectives d’avenir. Profitez de ces rencontres pour
              poser des questions et vous informer des diverses opportunités qui
              s’offrent à vous en termes de pratique médicale en région.
            </p>

            <p>
              Venez nous rencontrer pour valider les différents critères d’accès
              aux programmes de découverte et d’emploi des territoires qui vous
              intéressent. Ces derniers varient d’une région à l’autre en
              fonction des disponibilités, des périodes déterminées par chacune,
              du nombre de places disponibles et du type de stage offert.{" "}
              {/*La tournée des stages 2019, c’est un rendez-vous à ne pas manquer ! */}
            </p>
          </div>

          <div
            css={css`
              text-align: center;
              ${mediaQuery.lessThen(breakpoint)} {
                margin-bottom: -${fontSizes[9] / fontSizes[0]}em;
              }
              ${mediaQuery.greaterThen(breakpoint)} {
                grid-column: 1 / span 1;
              }
            `}
          >
            <img
              src={illustrationPath}
              alt=""
              css={css`
                width: calc(100% - 60px);
                max-width: 430px;
                margin-right: 60px;
              `}
            />
          </div>
        </section>

        {/* <section
          id="affiche"
          css={css`
            background-color: ${colors.offWhite};

            ${mediaQuery.lessThen(breakpoint)} {
              padding-top: ${fontSizes[9] / fontSizes[0]}em;
              padding-bottom: ${fontSizes[9] / fontSizes[0]}em;
            }

            ${mediaQuery.greaterThen(breakpoint)} {
              margin: ${fontSizes[9] / fontSizes[0]}em auto
                ${(fontSizes[9] / fontSizes[0]) * 2}em;
            }
          `}
        >
          <div
            css={css`
              ${wrapper.bolt()}

              ${mediaQuery.lessThen(breakpoint)} {
                margin-top: ${fontSizes[9] / fontSizes[0]}em;
              }

              ${mediaQuery.greaterThen(breakpoint)} {
                top: ${fontSizes[9] / fontSizes[0]}em;
                position: relative;
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                align-items: center;
              }
            `}
          >
            <IllustrationAfficheTournee
              css={css`
                grid-column: 2 / span 1;
                width: 100%;
                max-width: 475px;
                margin: auto;
                display: block;
              `}
            />

            <div
              css={css`
                grid-column: 3 / span 1;
                font-size: ${fontSizes[4] / fontSizes[0]}em;
                line-height: ${fontSizes[5] / fontSizes[4]};
                font-weight: bold;
                text-align: center;

                ${mediaQuery.lessThen(breakpoint)} {
                  margin-top: ${fontSizes[6] / fontSizes[4]}em;
                }

                img {
                  max-width: ${110 / fontSizes[4]}em;
                  margin-bottom: ${fontSizes[4] / fontSizes[4]}em;
                }
              `}
            >
              <img src={illustrationEye} alt="" />

              <p>
                Surveillez
                <br />
                l'affichage sur
                <br />
                votre campus!
              </p>
            </div>
          </div>
        </section> */}

        <section id="calendrier">
          <h2
            css={css`
              margin-top: ${fontSizes[8] / fontSizes[0]}em;
              text-align: center;
            `}
          >
            Calendrier
            <br />
            {calendarYear}
          </h2>

          {events.length < 1 && (
            <div
              css={css`
                text-align: center;
                font-size: ${fontSizes[4] / fontSizes[0]}em;
              `}
            >
              <p>Dates à venir.</p>
            </div>
          )}

          <div
            css={css`
              max-width: 1440px;
              margin: ${fontSizes[9] / fontSizes[0]}em auto;
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
              grid-auto-rows: 1fr;
              grid-auto-flow: dense;
              align-items: center;

              ${mediaQuery.greaterThen(breakpoint)} {
                grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
              }
            `}
          >
            {events.map((event) => (
              <EventCard {...event} />
            ))}

            <div>
              <a
                href="https://www.facebook.com/equipesarros/"
                css={css`
                  display: block;
                  text-align: center;
                  font-size: ${fontSizes[5] / fontSizes[1]}em;
                  line-height: ${fontSizes[6] / fontSizes[5]};
                  font-weight: bold;
                  text-decoration: none;
                  color: inherit;

                  img {
                    width: ${146 / fontSizes[5]}em;
                    display: block;
                    margin: auto auto ${fontSizes[6] / fontSizes[5]}em;
                  }
                `}
              >
                <img src={illustrationCellphoneFacebook} alt="" />
                Suivez la tournée sur&nbsp;Facebook!
              </a>
            </div>
          </div>
        </section>
      </article>
    </Layout>
  )
}

export default TourneeDesStagePage

export const query = graphql`
  query {
    events: allSanityEvent(
      filter: { type: { eq: "InternshipTour" } }
      sort: { fields: fromDate }
    ) {
      edges {
        node {
          address {
            city
            local
          }
          fromDate
          placeName
          toDate
          meta {
            slug {
              current
            }
          }
        }
      }
    }
  }
`
